// Copyright (c) Move Closer 2022.

import { Component, Vue } from 'vue-property-decorator'

import { Inject } from '../../../support'
import {
  CartItemData,
  ISiteService,
  ProductSneakPeakData,
  SiteServiceType,
  Variant as ProductVariant
} from '../../../contexts'

import { CartModel } from '../../checkout/contracts'

interface AnalyticsProduct {
  product: ProductSneakPeakData
  quantity?: number
}

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
@Component<CartAnalyticsMixin>({
  name: 'CartAnalyticsMixin'
})
export class CartAnalyticsMixin extends Vue {
  @Inject(SiteServiceType)
  protected readonly siteService!: ISiteService

  public getAnalyticsItem (item: AnalyticsProduct, currency?: string, quantity?: number) {
    const variant = this.getFirstVariant(item)
    const variantSlug = this.getFirstVariantKey(item)
    return {
      id: variant.sku,
      sku: variant.sku,
      parent_id: item.product.sku,
      variant: variantSlug,
      name: variant.name,
      currency: currency ?? this.siteService.getActiveSite().currency,
      category: variant.attributes.main_category,
      brand: variant.attributes.brand,
      price: variant.price.finalPrice,
      quantity: quantity ?? item.quantity,
      url: this.siteService.getActiveSiteAddress() + `${item.product.urlPath}`,
      image: item.product.variants && (Array.isArray(variant.images) &&
        variant.images.length > 0)
        ? this.siteService.getActiveSiteAddress() +
        variant.images[0].url : ''
    }
  }

  public getAnalyticsItems (cart: CartModel) {
    return cart.items?.map(item => this.getAnalyticsItem(item, cart.total.currency))
  }

  public getCartViewPayload (cart: CartModel) {
    return {
      currency: cart.total?.currency,
      value: cart.total?.value,
      totalQuantity: cart.items.length ?? 0,
      coupon: (cart.coupons && cart.coupons.length) ? cart.coupons[0].code : '',
      items: this.getAnalyticsItems(cart)
    }
  }

  public getCartAddPayload (addedProduct: CartItemData, quantity: number, cart: CartModel) {
    const variant = this.getFirstVariant(addedProduct)

    return {
      items: [this.getAnalyticsItem(addedProduct, cart.total.currency, quantity)],
      value: variant.price.finalPrice
    }
  }

  public getCartRemovePayload (itemToRemove: CartItemData, cart: CartModel | null) {
    const variant = this.getFirstVariant(itemToRemove)

    return {
      totalQuantity: cart?.items.length ?? 0,
      // This is for cart.remove event
      deletedItems: [this.getAnalyticsItem(itemToRemove, cart?.total.currency ?? '')],
      // This is for cart.status for synerise.
      items: cart ? this.getAnalyticsItems(cart) : [],
      value: variant.price.finalPrice
    }
  }

  public getProductViewPayload (variant: ProductVariant<string>, isGift: boolean = false) {
    return {
      value: variant.price.finalPrice,
      items: [
        {
          id: variant.sku,
          sku: variant.sku,
          name: variant.name,
          category: variant.attributes.mainCategory,
          brand: variant.attributes.brand,
          price: isGift ? 0 : variant.price.finalPrice,
          discount: isGift ? 0 : (variant.price.regularPrice - variant.price.finalPrice)
        }
      ]
    }
  }

  public getBaseCheckoutPayload (cart: CartModel | null) {
    return {
      currency: cart?.total?.currency,
      value: cart?.total?.value,
      coupon: (cart?.coupons[0]) ? cart?.coupons[0].code : '',
      items: cart ? this.getAnalyticsItems(cart) : []
    }
  }

  public getPurchasePayload (cart: CartModel | null, tax?: number) {
    return {
      value: cart?.total?.value,
      items: cart ? this.getAnalyticsItems(cart) : [],
      coupon: (cart?.coupons[0]) ? cart?.coupons[0].code : '',
      shipping: cart?.selectedShippingMethod?.price.value,
      tax: tax ?? cart?.getTotalTax(),
      discount: (cart?.discounts || []).reduce((sum, discount) => sum + discount.amount.value, 0),
      customer: {
        id: cart?.email ?? '',
        email: cart?.email ?? '',
        phone: cart?.billingAddress?.telephone ?? '',
        firstname: cart?.billingAddress?.firstname ?? '',
        lastname: cart?.billingAddress?.lastname ?? '',
        country: cart?.billingAddress?.countryCode ?? ''
      }
    }
  }

  public getFirstVariant (item: AnalyticsProduct): ProductVariant<string> {
    return Object.values(item.product.variants)[0]
  }

  private getFirstVariantKey (item: AnalyticsProduct): string {
    const key = Object.keys(item.product.variants)[0] ?? ''
    return (key !== '_') ? key : ''
  }
}
